type CopyTextOptions = {
  writeText?: ((text: string) => Promise<void>) | null;
  copyWithSelection?: (text: string) => boolean;
};

export async function copyTextToClipboard(text: string, options: CopyTextOptions = {}): Promise<boolean> {
  const writeText = options.writeText === undefined ? getClipboardWriter() : options.writeText;

  if (writeText) {
    try {
      await writeText(text);
      return true;
    } catch {
      // Browser clipboard permissions and support vary. Fall back to a temporary
      // selection when the async API rejects instead of failing the copy action.
    }
  }

  const copyWithSelection = options.copyWithSelection ?? copyTextWithSelection;
  try {
    return copyWithSelection(text);
  } catch {
    return false;
  }
}

function getClipboardWriter(): ((text: string) => Promise<void>) | null {
  if (typeof navigator === "undefined" || typeof navigator.clipboard?.writeText !== "function") return null;
  return navigator.clipboard.writeText.bind(navigator.clipboard);
}

function copyTextWithSelection(text: string): boolean {
  if (typeof document === "undefined" || !document.body || typeof document.execCommand !== "function") return false;

  const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.readOnly = true;
  textarea.tabIndex = -1;
  textarea.setAttribute("aria-hidden", "true");
  Object.assign(textarea.style, {
    position: "fixed",
    top: "0",
    left: "-9999px",
    fontSize: "16px",
    opacity: "0",
    pointerEvents: "none"
  });

  document.body.appendChild(textarea);
  let copied = false;
  try {
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  } finally {
    textarea.remove();
    try {
      activeElement?.focus({ preventScroll: true });
    } catch {
      // The previously focused element may have been removed while copying.
    }
  }
  return copied;
}
